import _ from "lodash";
import React, { useMemo } from "react";
import {
  SelectOption,
  SelectWithRecommendations,
  SimpleSelectProps,
} from "../../../common/components/input/Select";
import {
  LawfulBasis,
  ProcessingActivityDto,
} from "../../../common/service/server/dto/ProcessingActivityDto";
import {
  labelForLawfulBasis,
  useMandatoryLawfulBasisOptions,
  useRecommendedLawfulBasisOptions,
} from "../../hooks/useProcessingActivities";

const BASE_LAWFUL_BASIS_OPTIONS: LawfulBasis[] = [
  "COMPLY_WITH_LEGAL_OBLIGATIONS",
  "FULFILL_CONTRACTS",
  "LEGITIMATE_INTERESTS",
  "CONSENT",
];

export type LawfulBasisExtendedOptionsVisibility = {
  PUBLIC_INTEREST: boolean;
  VITAL_INTEREST_OF_INDIVIDUAL: boolean;
};

export const ProcessingActivityLawfulBasisSelect: React.FC<
  {
    activity?: ProcessingActivityDto;
    value: LawfulBasis[];
    onChange: (newBases: LawfulBasis[]) => Promise<void>;
    extendedOptionsVisibility?: LawfulBasisExtendedOptionsVisibility;
  } & Partial<Omit<SimpleSelectProps, "onChange" | "options" | "variant">>
> = ({
  activity,
  value,
  onChange,
  extendedOptionsVisibility = { PUBLIC_INTEREST: false, VITAL_INTEREST_OF_INDIVIDUAL: false },
  ...rest
}) => {
  let availableMenuOptions: LawfulBasis[] = [...BASE_LAWFUL_BASIS_OPTIONS];
  if (extendedOptionsVisibility["PUBLIC_INTEREST"]) {
    availableMenuOptions.push("PUBLIC_INTEREST");
  }
  if (extendedOptionsVisibility["VITAL_INTEREST_OF_INDIVIDUAL"]) {
    availableMenuOptions.push("VITAL_INTEREST_OF_INDIVIDUAL");
  }

  // Ensure that we always include the mandatory lawful bases in the dropdown,
  // in case an extended option is present in the mandatory items
  const finalMenuOptions: LawfulBasis[] = useMemo(
    () => _.union(availableMenuOptions, activity?.defaultMandatoryLawfulBases ?? []),
    [availableMenuOptions, activity],
  );

  const finalOptions: SelectOption[] = finalMenuOptions.map((o) => ({
    value: o,
    label: labelForLawfulBasis(o),
  }));

  const mandatoryOptions: SelectOption[] = useMandatoryLawfulBasisOptions(activity);
  const recommendedOptions: SelectOption[] = useRecommendedLawfulBasisOptions(activity);

  const handleChange = (value: any) => {
    // On autofill we get a stringified value.
    const bases: LawfulBasis[] =
      typeof value === "string" ? (value.split(",") as LawfulBasis[]) : (value as LawfulBasis[]);

    onChange(bases);
  };

  return (
    <SelectWithRecommendations
      options={finalOptions}
      value={value}
      mandatory={mandatoryOptions}
      recommendations={recommendedOptions}
      onChange={handleChange}
      multiple
      {...rest}
    />
  );
};
